import { Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { X } from 'tabler-icons-react';
import { OpenComponent } from '../../../enums/misc.enum';
import { retrieveOnboardingResponseToTab } from '../../../helpers/merchant-onboarding';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import {
  IPaginationMeta,
  OnboardingInformationByTab,
} from '../../../type/admin.types';
import {
  RetrieveOneMerchantBranchAccountsRequest,
  RetrieveOneMerchantBranchAccountsResponse,
  RetrieveOneMerchantBranchResponse,
} from '../../../type/entity.types';
import { ErrorResponse } from '../../../type/error-response.types';
import { BasicAccount } from '../../../type/retrieve-account.types';
import BackButton from '../../buttons/back-button.component';
import EntityInfo from './components/entity-info.component';

function BranchInfo(props: Props) {
  const { setComponentManager, selectedEntity } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [onboardingData, setOnboardingData] = useState<
    OnboardingInformationByTab | undefined
  >();
  const axiosPrivate = useAxiosPrivate();
  const [retrieveAccountsParams, setRetrieveAccountsParams] =
    useState<RetrieveOneMerchantBranchAccountsRequest>({
      page: 1,
      merchantBranchId: selectedEntity,
    });
  const [isAccountsLoading, setIsAccountsLoading] = useState(false);
  const [paginationData, setPaginationData] = useState<IPaginationMeta>();

  const [userData, setUserData] = useState<BasicAccount[]>();
  const [branchData, setBranchData] =
    useState<RetrieveOneMerchantBranchResponse>();

  const loadAccountsData = async () => {
    try {
      setIsLoading(true);

      const response = await axiosPrivate.post(
        `/web/merchant_branches/accounts`,
        retrieveAccountsParams
      );

      const data: RetrieveOneMerchantBranchAccountsResponse = response.data;

      setUserData(data.items);
      setPaginationData(data.metadata);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-retrieve-branch-accounts',
        message:
          error?.error ||
          err?.response?.data?.error ||
          'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsAccountsLoading(false);
    }
  };

  const paginationRequest = (page: number) => {
    setRetrieveAccountsParams((prev) => {
      return {
        ...prev,
        page,
      };
    });
  };

  useEffect(() => {
    const loadOnboardingData = async () => {
      try {
        setIsLoading(true);

        const response = await axiosPrivate.get(
          `/web/merchant_branches/${selectedEntity}`
        );
        const branchData: RetrieveOneMerchantBranchResponse = response.data;
        if (branchData.onboarding) {
          const tabOnboardingData = retrieveOnboardingResponseToTab(
            branchData.onboarding,
            branchData.onboarding.merchantId
          );

          setOnboardingData(tabOnboardingData);
        }

        setBranchData(branchData);
      } catch (err) {
      } finally {
        setIsLoading(false);
      }
    };

    loadOnboardingData();
    loadAccountsData();
  }, []);

  return (
    <>
      <BackButton
        handleClick={() => {
          setComponentManager(OpenComponent.HOME);
        }}
      />
      <Title order={2} my="lg">
        View Branch Information
      </Title>
      <EntityInfo
        isEntityActive={branchData?.isActive}
        disabledReason={branchData?.disabledReason}
        paginationRequest={paginationRequest}
        loadAccountsData={loadAccountsData}
        onboardingData={onboardingData}
        isLoading={isLoading}
        paginationData={paginationData}
        isAccountsLoading={isAccountsLoading}
        userData={userData}
        parentTypeId={branchData?.onboarding?.parentTypeId}
      />
    </>
  );
}

export default BranchInfo;

interface Props {
  setComponentManager: React.Dispatch<React.SetStateAction<OpenComponent>>;
  selectedEntity: number;
}
